import { useMemo } from 'react';

import { useAppSelector, useTranslate } from '@src/core/hooks';
import useCurrencyFormat from '@src/hooks/useCurrencyFormat';
import { appInfoSelector } from '@src/stores/app';

import { formatScheduleParts } from '../../core/formatScheduleParts';
import { getSummaryAdditionalRows } from '@src/features/transactables/summary/core/getSummaryAdditionalRows';
import { getSummaryTariffRows } from '@src/features/transactables/summary/core/getSummaryTariffRows';
import {
  getSummaryItemPackageDiscountCents,
  getSummaryItemTotalCents,
  type SummaryLineItem,
} from '@src/features/transactables/summary/core/summaryLineItem';

type UseSummaryItemCardParams = {
  item: SummaryLineItem;
};

export const useSummaryItemCard = (params: UseSummaryItemCardParams) => {
  const { item } = params;
  const { i18n } = useTranslate();
  const { formatCents } = useCurrencyFormat();
  const appInfo = useAppSelector(appInfoSelector);

  const tariffRows = useMemo(() => getSummaryTariffRows(item), [item]);
  const additionalRows = useMemo(() => getSummaryAdditionalRows(item), [item]);
  const schedule = useMemo(() => formatScheduleParts(item, i18n), [item, i18n]);
  const discountCents = useMemo(() => getSummaryItemPackageDiscountCents(item), [item]);
  const totalCents = useMemo(() => getSummaryItemTotalCents(item), [item]);
  const showSeller = item.product?.appId !== appInfo?.id;

  return {
    formatCents,
    product: item.product,
    tariffRows,
    additionalRows,
    schedule,
    discountCents,
    totalCents,
    showSeller,
  };
};

export default useSummaryItemCard;
